import { type FC, useContext } from "react";
import { useIntl } from "react-intl";
import { styled, CircularProgress, InputAdornment, OutlinedInput } from "@mui/material";

import { useScanToCreateInput } from "@/views/production/orders/use-scan-to-create-input";
import { useScanToTriggerInput } from "@/views/production/orders/use-scan-to-trigger";
import { ProductionContext } from "@/contexts/production";
import {
	isScanToTriggerConfigured,
	scanToProduceOptions,
} from "@/views/configuration/production-groups/manual-scan-to-produce-dropdown";

const StyledInput = styled(OutlinedInput)(({ theme }) => ({
	minWidth: "320px",
	backgroundColor: theme.palette.common.white,
}));

interface ScanInputProps {
	readonly triggerJobByLPN: (machineGroup: unknown, lpn: string) => void;
}

export const ScanInput: FC<ScanInputProps> = ({ triggerJobByLPN }) => {
	const intl = useIntl();
	const { currentMachineGroup, currentProductionGroup } = useContext(ProductionContext);
	const machineInErrorState = currentMachineGroup?.status === "Error";

	const scanToTriggerConfigured = isScanToTriggerConfigured(currentProductionGroup?.manualScanToProduce);
	const scanToCreateConfigured = currentProductionGroup?.manualScanToProduce === scanToProduceOptions.scanToCreate;

	const scanPlaceholder = intl.formatMessage({ id: "Ready to scan...", defaultMessage: "Ready to scan..." });

	const {
		inputRef: scanToCreateInputRef,
		onKeyUp: onScanToCreateInputKeyUp,
		isLoading: isScanToCreateInputLoading,
	} = useScanToCreateInput({ currentMachineGroup });

	const {
		inputRef: scanToTriggerInputRef,
		onKeyUp: onScanToTriggerInputKeyUp,
		isLoading: isScanToTriggerInputLoading,
	} = useScanToTriggerInput({ currentMachineGroup, triggerJobByLPN });

	if (!currentMachineGroup || !currentProductionGroup) {
		return null;
	}

	return (
		<>
			{scanToCreateConfigured === true && machineInErrorState === false && (
				<StyledInput
					size="small"
					disabled={isScanToCreateInputLoading}
					inputRef={scanToCreateInputRef}
					onKeyUp={onScanToCreateInputKeyUp}
					placeholder={scanPlaceholder}
					inputProps={{
						"data-cy": "scan-input",
					}}
					endAdornment={
						isScanToCreateInputLoading ? (
							<InputAdornment position="end">
								<CircularProgress size={20} />
							</InputAdornment>
						) : null
					}
				/>
			)}
			{scanToTriggerConfigured === true && machineInErrorState === false && (
				<StyledInput
					size="small"
					disabled={isScanToTriggerInputLoading}
					inputRef={scanToTriggerInputRef}
					onKeyUp={onScanToTriggerInputKeyUp}
					placeholder={scanPlaceholder}
					inputProps={{
						"data-cy": "scan-input",
					}}
					endAdornment={
						isScanToTriggerInputLoading ? (
							<InputAdornment position="end">
								<CircularProgress size={20} />
							</InputAdornment>
						) : null
					}
				/>
			)}
		</>
	);
};
